/* eslint-disable react/prop-types */
import { Star } from "@phosphor-icons/react";
import currency from "currency.js";
import { PropsWithChildren } from "react";
import { TFunction } from "i18next";
import { InstrumentTypeResponse } from "../../codegen-api";
import { COLORS, ICON_COLORS } from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { AssetResponse } from "../../utils/asset";
import { getAssetShortName } from "../../utils/asset/assets";
import {
  formatKnownPrice,
  formatKnownValue,
  knownNumberSortType,
  UNKNOWN_DISPLAY,
} from "../../utils/unknownNumbers";
import PriceChart from "../PriceChart";
import TickerLogo from "../shared/TickerLogo";
import { Align } from "../shared/Table/style";
import {
  AssetCellContainer,
  ChangeTag,
  PriceCell,
  WatchlistButton,
} from "./style";
import {
  IMarketsTableProps,
  IOptionMarketData,
  IPerpetualMarketData,
} from "./MarketsTable.types";

function AssetCell({
  asset,
  children,
}: PropsWithChildren<{ asset: AssetResponse }>) {
  return (
    <AssetCellContainer>
      <TickerLogo asset={asset} size={28} />
      {children || <span>{getAssetShortName(asset)}</span>}
    </AssetCellContainer>
  );
}

const defaultPriceTimestampValue: string[][] = [];
export const chartDimensions = {
  width: 40,
  height: 40,
};

export function buildMarketsColumns(
  type: IMarketsTableProps["type"],
  t: TFunction,
  isMobile: IMarketsTableProps["isMobile"],
  onWatchlist: IMarketsTableProps["onWatchlist"]
) {
  if (type === InstrumentTypeResponse.Perpetual || type === InstrumentTypeResponse.Spot) {
    const mobileCol: ITableColumn<IPerpetualMarketData>[] = [
      {
        title: t("market"),
        accessor: "market",
        Cell: ({ value, row }) => (
          <AssetCell asset={row.original.asset}>{value}</AssetCell>
        ),
      },
      {
        title: " ",
        accessor: "indexHistory",
        Header: () => <div />,
        Cell: ({ value }) => (
          <PriceChart
            timestampPriceData={(value?.length
              ? value
              : defaultPriceTimestampValue
            )
              .slice()
              .reverse()}
            chartStyle={"mini"}
            height={chartDimensions.height}
            width={chartDimensions.width}
          />
        ),
      },
      {
        title: t("price"),
        accessor: "price",
        align: "right",
        // Numeric, with an unknown (null) price grouped apart instead of as 0.
        sortType: knownNumberSortType,
        Cell: ({ value, row }) => {
          const { dailyChange } = row.original;
          return (
            <Align align="right">
              <PriceCell>
                <div>
                  {formatKnownPrice(
                    value,
                    Math.max(row.original.pricePrecision || 2, 2)
                  )}
                </div>
                {isMobile && (
                  <div>
                    <ChangeTag $value={dailyChange}>
                      {dailyChange === undefined
                        ? "-"
                        : `${(dailyChange * 100).toFixed(2)}%`}
                    </ChangeTag>
                  </div>
                )}
              </PriceCell>
            </Align>
          );
        },
      },
    ];

    const col: ITableColumn<IPerpetualMarketData>[] = [
      ...mobileCol,
      {
        title: t("daily_change"),
        accessor: "dailyChange",
        align: "right",
        sortType: knownNumberSortType,
        // An absent 24h change reads "-", not "0.00%". There is no reference
        // price to measure against, and a zero here is a claim, not a reading.
        Cell: ({ value }) => (
          <Align align="right">
            <ChangeTag $value={value}>
              {value === undefined
                ? "-"
                : `${value > 0 ? "+" : ""}${(value * 100).toFixed(2)}%`}
            </ChangeTag>
          </Align>
        ),
      },
      {
        title: t("daily_volume"),
        accessor: "dailyVolume",
        align: "right",
        // Same treatment as `price` and `dailyChange` above, which this column
        // was missing: an unknown volume must not sort as a real zero, mixed in
        // with markets that genuinely traded nothing.
        sortType: knownNumberSortType,
        // `value != null` never rejected anything, because the producers coerced
        // an absent volume to 0 before it got here -- so a market with no volume
        // data read "$0.00", a confident claim that it did not trade.
        Cell: ({ value }) => (
          <Align align="right">
            {value === undefined || value === null
              ? UNKNOWN_DISPLAY
              : currency(value).format()}
          </Align>
        ),
      },
    ];

    const watchlistCol: ITableColumn<IPerpetualMarketData> = {
      title: " ",
      accessor: "id",
      Header: () => <div />,
      Cell: ({ row }) => (
        <Align
          align="right"
          style={{
            marginRight: SPACING.two,
          }}
        >
          <WatchlistButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onWatchlist(row.original);
            }}
          >
            <Star
              weight={row.original.isWatchlist ? "fill" : "regular"}
              fill={
                row.original.isWatchlist
                  ? COLORS.highlight.one
                  : ICON_COLORS.three
              }
            />
          </WatchlistButton>
        </Align>
      ),
    };

    return isMobile ? [...mobileCol, watchlistCol] : [...col, watchlistCol];
  }

  const mobileCol: ITableColumn<IOptionMarketData>[] = [
    {
      title: t("underlying_asset"),
      accessor: "asset",
      Cell: ({ value }) => <AssetCell asset={value} />,
    },
    {
      title: t("oi"),
      accessor: "oi",
      align: "right",
      // Unknown (null index price) is "-" and sorts apart from 0.
      sortType: knownNumberSortType,
      Cell: ({ value }) => (
        <Align align="right">{formatKnownValue(value)}</Align>
      ),
    },
  ];

  const col: ITableColumn<IOptionMarketData>[] = [
    mobileCol[0],
    {
      title: " ",
      accessor: "indexHistory",
      Header: () => <div />,
      Cell: ({ value }) => (
        <PriceChart
          timestampPriceData={(value?.length
            ? value
            : defaultPriceTimestampValue
          )
            .slice()
            .reverse()}
          chartStyle={"mini"}
          height={chartDimensions.height}
          width={chartDimensions.width}
        />
      ),
    },
    {
      title: t("put_call_ratio"),
      accessor: "putCallRatio",
      align: "right",
      Cell: ({ value }) => <Align align="right">{value.toFixed(2)}</Align>,
    },
    mobileCol[1],
    {
      title: t("daily_volume"),
      accessor: "dailyVolume",
      align: "right",
      Cell: ({ value }) => (
        <Align align="right">{value ? currency(value).format() : "-"}</Align>
      ),
    },
  ];

  const watchlistCol: ITableColumn<IOptionMarketData> = {
    title: " ",
    accessor: "id",
    Header: () => <div />,
    Cell: ({ row }) => (
      <Align
        align="right"
        style={{
          marginRight: SPACING.two,
        }}
      >
        <WatchlistButton
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onWatchlist(row.original);
          }}
        >
          <Star
            weight={row.original.isWatchlist ? "fill" : "regular"}
            fill={
              row.original.isWatchlist
                ? COLORS.highlight.one
                : ICON_COLORS.three
            }
          />
        </WatchlistButton>
      </Align>
    ),
  };

  return isMobile ? [...mobileCol, watchlistCol] : [...col, watchlistCol];
}
